import { useLayoutEffect, useRef } from 'react';
import { createTimeline, stagger } from 'animejs';
import { prefersReducedMotion } from '../utils/motion';

/**
 * Staggered hero entrance: copy, CTAs, then the product showcase card.
 */
export function useHeroEntrance() {
  const heroRowRef = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const heroVisualRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const row = heroRowRef.current;
    const content = heroContentRef.current;
    const visual = heroVisualRef.current;
    if (!row || !content || !visual) return;

    const title = content.querySelector<HTMLElement>('[data-hero="title"]');
    const simpl = content.querySelector<HTMLElement>('[data-hero="simpl"]');
    const subtitle = content.querySelector<HTMLElement>('[data-hero="subtitle"]');
    const ctas = row.querySelectorAll<HTMLElement>('[data-hero="cta"]');
    const processorLabel = visual.querySelector<HTMLElement>('[data-hero="processor-label"]');
    const heroCard = visual.querySelector<HTMLElement>('[data-hero="hero-card"]');

    const all = [title, simpl, subtitle, processorLabel, heroCard, ...Array.from(ctas)].filter(
      Boolean
    ) as HTMLElement[];

    if (prefersReducedMotion()) {
      all.forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    all.forEach((el) => {
      el.style.opacity = '0';
    });

    const tl = createTimeline({ defaults: { ease: 'outExpo' } });

    if (title) {
      tl.add(title, { opacity: [0, 1], y: [28, 0], duration: 850 });
    }
    if (simpl) {
      tl.add(simpl, { opacity: [0, 1], scale: [0.88, 1], duration: 720, ease: 'outBack' }, '-=560');
    }
    if (subtitle) {
      tl.add(subtitle, { opacity: [0, 1], y: [22, 0], duration: 780 }, '-=480');
    }
    if (processorLabel) {
      tl.add(processorLabel, { opacity: [0, 1], y: [14, 0], duration: 620 }, '-=420');
    }
    if (heroCard) {
      tl.add(heroCard, {
        opacity: [0, 1],
        scale: [0.9, 1],
        duration: 1050,
        ease: 'outQuart',
      }, '-=680');
    }
    const ctaEls = Array.from(ctas);
    if (ctaEls.length) {
      tl.add(ctaEls, {
        opacity: [0, 1],
        y: [18, 0],
        duration: 680,
        delay: stagger(75),
        ease: 'outCubic',
      }, '-=520');
    }

    return () => {
      tl.revert();
    };
  }, []);

  return { heroRowRef, heroContentRef, heroVisualRef };
}
